import {
  DialogFlowResult,
  ListeningStatus,
  ListeningStatusKey,
  SpeechRecognitionResponse,
} from 'src/app/data/type/audio';
import { getLocaleFromPath } from 'src/app/utils/pathname';
import AudioStream from './audio-stream';

interface RemoteSpeechRecognitionConstructor {
  onStatusChanged: (status: ListeningStatusKey) => void;
  onSpeechResultsChanged: (recognition: SpeechRecognitionResponse) => void;
  onDialogFlowResultReceived: (dialogflowResult: DialogFlowResult) => void;
  socketUrl: string;
}

export default class RemoteSpeechRecognition {
  private socketUrl: string;
  private webSocketClient: WebSocket;
  private stream: AudioStream;
  private onStatusChanged: (status: ListeningStatusKey) => void;
  private onSpeechResultsChanged: (recognition: SpeechRecognitionResponse) => void;
  private onDialogFlowResultReceived: (dialogflowResult: DialogFlowResult) => void;

  constructor({
    onStatusChanged,
    onSpeechResultsChanged,
    onDialogFlowResultReceived,
    socketUrl,
  }: RemoteSpeechRecognitionConstructor) {
    this.socketUrl = socketUrl;
    this.onStatusChanged = onStatusChanged;
    this.onSpeechResultsChanged = onSpeechResultsChanged;
    this.onDialogFlowResultReceived = onDialogFlowResultReceived;

    this.stream = new AudioStream({
      handleStart: () => this.onStatusChanged(ListeningStatus.LISTENING),
      handleStop: () => this.onStatusChanged(ListeningStatus.END),
      handleStream: this.handleStream.bind(this),
    });

    this.connectSocket();
  }

  handleStream(buffer: Int16Array) {
    const socketIsClosed = this.webSocketClient.readyState === this.webSocketClient.CLOSED;
    const socketIsConnecting = this.webSocketClient.readyState === this.webSocketClient.CONNECTING;
    if (socketIsConnecting) return;

    if (socketIsClosed) {
      this.connectSocket();
    } else {
      this.webSocketClient.send(buffer);
    }
  }

  connectSocket() {
    const locale = getLocaleFromPath();
    this.webSocketClient = new WebSocket(`${this.socketUrl}?locale=${locale}`);
    this.webSocketClient.onmessage = this.handleSocketResponse.bind(this);
  }

  onDestroy(): void {
    this.webSocketClient.close();
  }

  handleSocketResponse(message: MessageEvent<string>) {
    const data = JSON.parse(message.data);

    // If it has intent information, it's a response from the DF flow
    if (!!data.intent) this.onDialogFlowResultReceived(data);
    else this.onSpeechResultsChanged(data);
  }

  public start(deviceId: string, callback?: () => void) {
    this.onStatusChanged(ListeningStatus.LOADING);

    navigator.mediaDevices
      .getUserMedia({
        audio: {
          deviceId,
        },
      })
      .then((mediaStream) => {
        this.stream.startListening(mediaStream);
        callback && callback();
      })
      .catch(() => this.onStatusChanged(ListeningStatus.ERROR));
  }

  public stop(transcript: string) {
    this.stream.stopListening();
    // Give time to keep processing the last word/s
    setTimeout(() => this.webSocketClient.send(transcript), 100);
  }
}
